import type { Task } from './async';
import { Guard } from './async';

class Ongoing<T> {
  completed = false;
  constructor(readonly promise: Promise<T>) {
  }
}

/**
 * Generates a sequence of items using promise constructors (tasks) from an
 * input iterator created on demand in order to achieve a certain degree of
 * concurrency. Returns items in the order that their respective task were
 * retrieved from the input iterator, rather than in the order the promises
 * completed.
 *
 *     Max concurrency: 2
 *     Input:
 *         -----(A)--(B)-----------(C)---------------------------------->
 *               |    |             |
 *               |    |            (C)~~~~~~~~~~~~~~~~~~~~~~~(C')-->
 *               |    |
 *               |   (B)~~~~~~~~(B')-->
 *               |
 *              (A)~~~~~~~~~~~~~~~~~~~~~~~~~~~~(A')-->
 *
 *     Concurrency:
 *         [0-0][1-1][2----------2][2------------2][1----------1][0----0]
 *
 *     Output:
 *         ------------------------------------(A')-(B')-----(C')------->
 */
export function parallelTasks<T>(gen: AsyncIterable<Task<T>>, concurrency: number): AsyncIterableIterator<T> {
  let lastStarted = 0;
  let nextOut = 1;
  let done = false;
  const ongoingTasks: Record<number, Ongoing<T>> = {};
  const moreCompleted = new Guard();
  const mayWantMore = new Guard();
  let exception: any;
  const enqueue = async () => {
    await mayWantMore.wait();
    for await (const task of gen) {
      ++lastStarted;
      while (lastStarted + 1 - nextOut >= concurrency) {
        await mayWantMore.wait();
      }
      const promise = task();
      const ongoing = new Ongoing(promise);

      /*
        It is important to chain .catch() before chaining .finally(). Otherwise
        .finally() will create another Promise instance that also needs to be
        caught in order to avoid UnhandledPromiseRejectionWarning.
      */
      promise.catch(_ => {}).finally(() => {
        ongoing.completed = true;
        moreCompleted.notify();
      });
      ongoingTasks[lastStarted] = ongoing;
    }
  };

  enqueue().catch(ex => exception = ex).finally(() => {
    done = true;
    moreCompleted.notify();
  });
  return {
    [Symbol.asyncIterator]() {
      return this;
    },

    async next(): Promise<IteratorResult<T>> {
      mayWantMore.notify();
      while (true) {
        if (exception) {
          throw exception;
        }
        const n = ongoingTasks[nextOut];
        if (n == null) {
          if (done) {
            return { done, value: undefined };
          }
          await moreCompleted.wait();
          continue;
        }
        const value = await n.promise;
        delete ongoingTasks[nextOut];
        ++nextOut;
        mayWantMore.notify();
        return { value };
      }
    }
  };
}
